import { Link } from "react-router-dom";
import { Sofa, CalendarClock, Settings2, ClipboardList } from "lucide-react";
import { TourCard } from "../components/site/TourCard";
import { SectionTitle } from "../components/site/PublicLayout";
import { useFetch } from "../lib/hooks";
import { FAMILY_DESTINATIONS } from "../lib/site";
import useSeo from "../lib/useSeo";

const VALUES = [
  { Icon: Sofa, t: "Comfort first", d: "Stays and travel pace chosen with children and elders in mind." },
  { Icon: CalendarClock, t: "Flexible dates", d: "Travel on dates that suit your family." },
  { Icon: Settings2, t: "Customisable", d: "Add or remove days, activities and sightseeing." },
  { Icon: ClipboardList, t: "Clear planning", d: "A clear day-wise plan and inclusions before you book." },
];

export default function FamilyTours() {
  useSeo({ title: "Family Tours — Uttarakhand, Himachal, Kashmir & more | Wandervibe Journeys", description: "Comfortable, flexible family holidays across Uttarakhand, Himachal Pradesh, Kashmir, Ladakh, Spiti Valley and Rajasthan.", path: "/family-tours" });
  const { data: tours } = useFetch("/tours?type=family");
  return (
    <>
      <section className="relative overflow-hidden bg-[#171717] text-white">
        <img src="/images/family.jpg" alt="Family walking in a green mountain valley" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="container-x relative py-20 md:py-28">
          <p className="eyebrow">Family Tours</p>
          <h1 className="mt-3 max-w-2xl text-4xl sm:text-5xl lg:text-6xl">Holidays the whole family will remember</h1>
          <p className="mt-5 max-w-xl text-base text-white/85 md:text-lg">Comfort, convenience and flexible travel, with every detail planned clearly.</p>
          <Link to="/contact-us?category=Family%20Tour" className="btn-brand mt-8" data-testid="family-hero-plan-btn">Plan a Family Trip</Link>
        </div>
      </section>
      <section className="container-x py-16">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map(({ Icon, t, d }) => (
            <div key={t} className="rounded-2xl border border-slate-200 bg-white p-6">
              <Icon className="h-6 w-6 text-[#FD691B]" /><h3 className="mt-4 font-display text-base font-bold">{t}</h3><p className="mt-1.5 text-sm text-slate-600">{d}</p>
            </div>
          ))}
        </div>
      </section>
      {tours?.length > 0 && (
        <section className="container-x pb-16" data-testid="family-tours-list">
          <SectionTitle eyebrow="Family packages" title="Family tour packages" />
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">{tours.map((t) => <TourCard key={t.slug} tour={t} family />)}</div>
        </section>
      )}
      <section className="bg-white py-20" data-testid="family-destinations">
        <div className="container-x">
          <SectionTitle eyebrow="Destinations" title="Where would your family like to go?" sub="Tell us your destination and dates — we'll plan a family-friendly itinerary around you." />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FAMILY_DESTINATIONS.map((d) => (
              <article key={d.name} className="card-lift overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`family-dest-${d.name.toLowerCase().replace(/\s/g, "-")}`}>
                <img src={d.image} alt={`${d.name} landscape`} loading="lazy" className="aspect-[16/10] w-full object-cover" />
                <div className="flex items-center justify-between gap-4 p-5">
                  <div><h3 className="font-display text-lg font-bold">{d.name}</h3><p className="text-xs text-slate-500">Custom family itinerary on request</p></div>
                  <Link to={`/contact-us?category=Family%20Tour&destination=${encodeURIComponent(d.name)}`} className="btn-brand !px-4 !py-2.5" data-testid={`family-dest-enquire-${d.name.toLowerCase().replace(/\s/g, "-")}`}>Enquire</Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
